import { defineStore } from 'pinia'
import { computed, ref, Ref } from 'vue'
import Api from '@/config/api.helper'
import { Plano } from '@/types/plugins/session'
import { useEmpresaStore } from './empresaStore'

interface PlanoStore {
  plano: Ref<Plano | null>
  isLoading: Ref<boolean>
  hasPlano: Ref<boolean>
  hasModule(modulo: string): boolean
  setPlano(newPlano: Plano | null): void
  clearPlano(): void
  fetchPlano(empresaLogadaId?: string): Promise<Plano | null>
  initialize(): Promise<void>
}

const PLANO_STORAGE_KEY = 'planoStore.plano'

function hasActiveSession(): boolean {
  const token = localStorage.getItem('token')
  return typeof token === 'string' && token.trim().length > 0
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
}

function normalizePlanoPayload(payload: unknown): Plano | null {
  if (!isRecord(payload)) {
    return null
  }

  if (
    typeof payload.uuid_pla_id !== 'string' ||
    typeof payload.pla_descricao !== 'string'
  ) {
    return null
  }

  const modulos = Array.isArray(payload.modulos)
    ? payload.modulos.filter((modulo): modulo is string => typeof modulo === 'string')
    : []

  const dadosFranquia = Array.isArray(payload.dados_franquia)
    ? (payload.dados_franquia as Plano['dados_franquia'])
    : []

  return {
    uuid_pla_id: payload.uuid_pla_id,
    pla_descricao: payload.pla_descricao,
    pla_valor_bonus: Number(payload.pla_valor_bonus ?? 0),
    pla_gerar_bonus: Boolean(payload.pla_gerar_bonus),
    pla_minimo_recarga: Number(payload.pla_minimo_recarga ?? 0),
    pla_tipo_valor_bonus: Boolean(payload.pla_tipo_valor_bonus),
    new_business_model: Boolean(payload.new_business_model),
    is_custom_plan: Boolean(payload.is_custom_plan),
    monthly_price: Number(payload.monthly_price ?? 0),
    modulos,
    dados_franquia: dadosFranquia,
  } as Plano
}

function extractPlanoPayload(responseData: unknown): unknown {
  if (!isRecord(responseData)) {
    return responseData
  }

  if ('plano' in responseData) {
    return responseData.plano
  }

  if ('data' in responseData) {
    const data = responseData.data

    if (isRecord(data) && 'plano' in data) {
      return data.plano
    }

    return data
  }

  return responseData
}

function getEmpresaLogadaHeader(): string | undefined {
  const empresaStore = useEmpresaStore()
  const uuidEmpId = empresaStore.empresa?.uuid_emp_id

  if (typeof uuidEmpId === 'string' && uuidEmpId) {
    return uuidEmpId
  }

  return undefined
}

export const usePlanoStore = defineStore('plano', (): PlanoStore => {
  const plano = ref<Plano | null>(null)
  const isLoading = ref<boolean>(false)
  const hasModule = (modulo: string): boolean => {
    return plano.value?.modulos?.includes(modulo) ?? false
  }
  const hasPlano = computed((): boolean => !!plano.value)

  function setPlano(newPlano: Plano | null): void {
    plano.value = newPlano

    if (newPlano) {
      localStorage.setItem(PLANO_STORAGE_KEY, JSON.stringify(newPlano))
      return
    }

    localStorage.removeItem(PLANO_STORAGE_KEY)
  }

  function clearPlano(): void {
    setPlano(null)
  }

  async function fetchPlano(empresaLogadaId?: string): Promise<Plano | null> {
    if (!hasActiveSession()) {
      return null
    }

    isLoading.value = true

    try {
      const empresaLogada = empresaLogadaId ?? getEmpresaLogadaHeader()

      if (!empresaLogada) {
        console.warn(
          'Requisição de plano ignorada: uuid da empresa logada ausente para montar header empresalogada.'
        )
        return null
      }

      const response = await Api.get<unknown>('v2/plano', {
        headers: {
          empresalogada: empresaLogada,
        },
      })

      const payload = extractPlanoPayload(response.data)
      const normalizedPlano = normalizePlanoPayload(payload)

      if (!normalizedPlano) {
        clearPlano()
        return null
      }

      setPlano(normalizedPlano)
      return normalizedPlano
    } catch (error) {
      console.error('Erro ao buscar dados do plano:', error)
      return null
    } finally {
      isLoading.value = false
    }
  }

  async function initialize(): Promise<void> {
    if (!hasActiveSession()) {
      return
    }

    let cachedPlano = localStorage.getItem(PLANO_STORAGE_KEY)

    if (!cachedPlano) {
      await fetchPlano()
      cachedPlano = localStorage.getItem(PLANO_STORAGE_KEY)

      if (!cachedPlano) {
        return
      }
    }

    try {
      const parsedPlano = JSON.parse(cachedPlano) as unknown
      const normalizedPlano = normalizePlanoPayload(parsedPlano)

      if (!normalizedPlano) {
        clearPlano()
        await fetchPlano()
        return
      }

      plano.value = normalizedPlano
    } catch (error) {
      console.error('Erro ao carregar dados do plano em cache:', error)
      clearPlano()
      await fetchPlano()
    }
  }

  return {
    plano,
    isLoading,
    hasModule,
    hasPlano,
    setPlano,
    clearPlano,
    fetchPlano,
    initialize,
  }
})
